import { useMemo, useState } from "react";
import { useNavigate } from "react-router";

import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Radio,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { format } from "date-fns";

import { AppointmentResponse, ClientResponse } from "@/api";
import { toAllDayStart } from "@/common/components/appointments/shared/AppointmentForm.utils";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { useAppointmentLabels } from "@/common/hooks/useAppointmentLabels";
import { useDropdownOptions } from "@/common/hooks/useDropdownOptions";
import { clientPath } from "@/router/ClientRoutes";
import { LanguageType, useTranslation } from "@/translations";

import { useAssignAppointment } from "./hooks/useAssignAppointment";
import { useAvailableAppointmentsQuery } from "./hooks/useAvailableAppointmentsQuery";

interface Props {
  client: ClientResponse;
  onClose: () => void;
}

const ALL = "__ALL__";

export function Assign1bAppointmentDialog({ client, onClose }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { language } = useLanguageContext();

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const [startDate, setStartDate] = useState<Date>(today);
  const [locationFilter, setLocationFilter] = useState<string>(ALL);
  const [kzzFilter, setKzzFilter] = useState<string>(ALL);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(null);

  const { appointments, isLoading } = useAvailableAppointmentsQuery(startDate);
  const { appointmentLocations } = useDropdownOptions();
  const { assign, isAssigning } = useAssignAppointment();

  const kzzOptions = useMemo(() => {
    const codes = new Set<string>();
    for (const a of appointments) {
      if (a.assignedCounselorUserCode) {
        codes.add(a.assignedCounselorUserCode);
      }
    }
    return Array.from(codes).sort();
  }, [appointments]);

  const filtered = useMemo(() => {
    const now = new Date();
    return appointments.filter((a) => {
      if (new Date(a.startTimeUtc) <= now) {
        return false;
      }
      if (locationFilter !== ALL && (a.location ?? "") !== locationFilter) {
        return false;
      }
      if (kzzFilter !== ALL && (a.assignedCounselorUserCode ?? "") !== kzzFilter) {
        return false;
      }
      return true;
    });
  }, [appointments, locationFilter, kzzFilter]);

  const subtitle = useMemo(() => {
    const [first, ...rest] = filtered;
    if (!first) {
      return t("calendar.assign1b.subtitleEmpty");
    }
    let earliest = first.startTimeUtc;
    let latest = first.startTimeUtc;
    for (const a of rest) {
      if (a.startTimeUtc < earliest) {
        earliest = a.startTimeUtc;
      }
      if (a.startTimeUtc > latest) {
        latest = a.startTimeUtc;
      }
    }
    return t("calendar.assign1b.subtitle", {
      count: filtered.length,
      from: format(earliest, "dd.MM.yyyy", { locale: locales[language] }),
      to: format(latest, "dd.MM.yyyy", { locale: locales[language] }),
    });
  }, [filtered, language, t]);

  const openClient = () => {
    void navigate(clientPath.byId({ id: client.clientId }));
  };

  const handleAssign = () => {
    if (!selectedAppointmentId) {
      return;
    }
    assign({ appointmentId: selectedAppointmentId, clientId: client.clientId }, { onSuccess: openClient });
  };

  const canSave = selectedAppointmentId !== null && !isAssigning;

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      slotProps={{ paper: { sx: { height: "80vh", display: "flex", flexDirection: "column" } } }}
    >
      <DialogTitle sx={{ p: 5, pb: 2, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Box>
          <Typography variant="h1">
            {t("calendar.assign1b.title", { firstName: client.firstName, lastName: client.lastName })}
          </Typography>
          <Typography variant="subtitle1" sx={{ mt: 1 }}>
            {subtitle}
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label={t("common.close")}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 5, pt: 5, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={3} sx={{ mt: 2, mb: 3, flexShrink: 0 }}>
          <DatePicker
            label={t("calendar.assign1b.filters.date")}
            value={startDate}
            onChange={(v) => {
              if (v) {
                setStartDate(v);
              }
            }}
            minDate={today}
            slotProps={{ textField: { fullWidth: true, size: "small", sx: { minWidth: 260 } } }}
          />
          <Autocomplete
            disableClearable
            fullWidth
            size="small"
            options={[ALL, ...appointmentLocations]}
            value={locationFilter}
            onChange={(_, v) => setLocationFilter(v)}
            getOptionLabel={(o) => (o === ALL ? t("calendar.assign1b.filters.locationAll") : o)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("calendar.assign1b.filters.location")}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            )}
            sx={{ minWidth: 260 }}
          />
          <Autocomplete
            disableClearable
            fullWidth
            size="small"
            options={[ALL, ...kzzOptions]}
            value={kzzFilter}
            onChange={(_, v) => setKzzFilter(v)}
            getOptionLabel={(o) => (o === ALL ? t("calendar.assign1b.filters.kzzAll") : o)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("calendar.assign1b.filters.kzz")}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            )}
            sx={{ minWidth: 260 }}
          />
        </Stack>

        <Box sx={{ flexGrow: 1, overflowY: "auto", minHeight: 0 }}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress aria-label={t("calendar.assign1b.loading")} />
            </Box>
          ) : filtered.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
              {t("calendar.assign1b.emptyList")}
            </Typography>
          ) : (
            <Box role="radiogroup" aria-label={t("calendar.assign1b.selectAppointment")}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ fontWeight: 700 }} />
                    <TableCell sx={{ fontWeight: 700 }}>{t("calendar.assign1b.columns.date")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("calendar.assign1b.columns.time")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("calendar.assign1b.columns.purpose")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("calendar.assign1b.columns.location")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("calendar.assign1b.columns.kzz")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((a, index) => (
                    <AppointmentRow
                      key={a.appointmentId}
                      appointment={a}
                      index={index}
                      selected={selectedAppointmentId === a.appointmentId}
                      onSelect={() => setSelectedAppointmentId(a.appointmentId)}
                      language={language}
                    />
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{ p: 5, pt: 0, gap: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        <Button variant="outlined" onClick={onClose} disabled={isAssigning}>
          {t("calendar.assign1b.cancel")}
        </Button>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button variant="outlined" onClick={openClient} disabled={isAssigning}>
            {t("calendar.assign1b.skip")}
          </Button>
          <Button variant="contained" onClick={handleAssign} disabled={!canSave}>
            {t("calendar.assign1b.save")}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

interface RowProps {
  appointment: AppointmentResponse;
  index: number;
  selected: boolean;
  onSelect: () => void;
  language: LanguageType;
}

function AppointmentRow({ appointment, index, selected, onSelect, language }: RowProps) {
  const { t } = useTranslation();
  const { purposeLabelOf } = useAppointmentLabels();
  const dateText = format(appointment.startTimeUtc, "EE, dd.MM.yyyy", { locale: locales[language] });
  // All-day events are stored at midnight; surface the standard work-day start hour instead of 00:00.
  const startTime = appointment.isAllDay ? toAllDayStart(new Date(appointment.startTimeUtc)) : appointment.startTimeUtc;
  const timeText = `${format(startTime, "HH:mm", { locale: locales[language] })} ${t("calendar.assign1b.timeSuffix")}`;

  return (
    <TableRow
      hover
      sx={{
        height: 56,
        backgroundColor: index % 2 === 0 ? "background.paper" : "action.hover",
      }}
    >
      <TableCell padding="checkbox">
        <Radio
          checked={selected}
          onChange={onSelect}
          slotProps={{ input: { "aria-label": t("calendar.assign1b.selectAppointment") } }}
        />
      </TableCell>
      <TableCell>{dateText}</TableCell>
      <TableCell>{timeText}</TableCell>
      <TableCell>{purposeLabelOf(appointment.purposeKey)}</TableCell>
      <TableCell>{appointment.location}</TableCell>
      <TableCell>{appointment.assignedCounselorUserCode ?? ""}</TableCell>
    </TableRow>
  );
}
